import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import { HidingTooltip } from 'ui/Tooltip';

interface ISimpleTabsOption {
  title: string;
  name: string;
  styles: string;
  styleObject?: React.CSSProperties; // for extremely specific widths
  renderContent: () => React.ReactElement;
  hasUnsavedChanges?: boolean;
}

interface ISimpleTabsProps {
  tabConfig: ISimpleTabsOption[];
  className?: string;
  tabSelectorClassName?: string;
  showBorders?: boolean;
  forceEqualTabWidths?: boolean;
  activeTabIndex?: number;
  onTabChange?: (index: number) => void;
}

const unsavedChangesRenderTooltipContent = () => {
  return <div className="font-pt-sans text-13px p-2 text-black font-normal">This tab has unsaved changes.</div>;
};

export const SimpleTabs: React.FC<ISimpleTabsProps> = ({
  tabConfig,
  className,
  tabSelectorClassName,
  showBorders = true,
  forceEqualTabWidths = false,
  activeTabIndex,
  onTabChange,
}) => {
  const [internalSelectedTabIndex, setInternalSelectedTabIndex] = useState(0);
  const selectedTabIndex = activeTabIndex ?? internalSelectedTabIndex;
  const makeHandleTabChange = useCallback((tabIndex: number) => () => {
    setInternalSelectedTabIndex(tabIndex);
    onTabChange?.(tabIndex);
  }, [onTabChange]);

  // Calculate equal width percentage if forceEqualTabWidths is true
  const equalWidthPercentage = forceEqualTabWidths ? `${100 / tabConfig.length}%` : undefined;

  return (
    <div className={classnames('tabs pt-25px pb-35px w-1280px mx-auto', className)}>
      <div className={classnames('tab-selector flex mt-5', tabSelectorClassName)}>
        {tabConfig.map((tab, index) => (
          <span
            key={tab.name}
            style={forceEqualTabWidths ? { width: equalWidthPercentage, ...tab.styleObject } : tab.styleObject || {}}
            onClick={makeHandleTabChange(index)}
            className={classnames(
              `${tab.name} font-pt-sans text-[15px] text-black-true text-center border border-solid border-gray-40 border-r-0 last-of-type:border-r rounded rounded-b-none py-[13px] px-[14px] not-active select-none cursor-pointer relative`,
              forceEqualTabWidths ? '' : tab.styles,
              {
                'bg-ivory shadow-[0px_-5px_5px_0px_#00000008_inset]': index !== selectedTabIndex,
                'border-b-0 font-bold text-teal-100 active': index === selectedTabIndex,
              }
            )}
          >
            {tab.title}
            {tab.hasUnsavedChanges && (
              <HidingTooltip renderTooltipContent={unsavedChangesRenderTooltipContent} position="right">
                <span className="absolute -top-[40px] -right-5 text-white w-6 h-6 bg-yellow-60 rounded-full text-lg z-10">
                  !
                </span>
              </HidingTooltip>
            )}
          </span>
        ))}
        {!forceEqualTabWidths && (
          <div
            className={classnames('w-full border-t-0 border-r-0 border-l-0', {
              'border-b border-solid border-gray-40': showBorders,
            })}
          />
        )}
      </div>
      {tabConfig.map((tab, index) => {
        if (index === selectedTabIndex) {
          return (
            <div
              key={tab.name}
              className={classnames(`${tab.name} flex pt-15px`, {
                'border border-solid border-gray-40 border-t-0 pb-30px px-5': showBorders,
              })}
            >
              {tab.renderContent()}
            </div>
          );
        }
      })}
    </div>
  );
};
